import {
  Component,
  OnInit,
  AfterViewInit,
  ViewEncapsulation,
  Input,
  ViewChild,
  ViewContainerRef,
} from "@angular/core";
import { ChartOptions, ChartType, ChartDataSets, PluginServiceGlobalRegistration, PluginServiceRegistrationOptions } from "chart.js";
import { Label, Color, BaseChartDirective } from "ng2-charts";
import * as Chart from 'chart.js';

import { ZoneComponent } from "@/shared-module/components";
import { MultiChartDatasource } from "@/shared-module/models";
import { DialogService } from "@/shared-module/services";

/**
 * MultiChart2LevelsLegendComponent
 *
 * Displays a combined bar and line chart with two-level x-axis (e.g., "A B C D" and "P4 P5").
 *
 * Special Feature - Line Point Alignment:
 * This component uses a custom Chart.js plugin to horizontally offset line series bullets
 * to align with their corresponding columns (A, B, C, D) without modifying the underlying
 * data structure or x-axis configuration. This is achieved by:
 *   1. Creating a Chart.js plugin (lineOffsetPlugin) that manipulates point x-coordinates
 *   2. Registering the plugin locally (ngAfterViewInit) to avoid affecting other charts
 *   3. Using fixed pixel offsets that adjust based on zoom state
 *
 * The bars and x-axis remain untouched, only line point positions are visually adjusted.
 */
@Component({
  selector: "app-multichart2levelslegend",
  templateUrl: "multichart2levelslegend.component.html",
  encapsulation: ViewEncapsulation.None,
})
export class MultiChart2LevelsLegendComponent implements ZoneComponent, OnInit, AfterViewInit {
  constructor(
    private viewContainerRef: ViewContainerRef,
    private dialogService: DialogService
  ) {}
  @Input() data: MultiChartDatasource;
  @Input() width: number;
  @Input() needpaddingLeft: boolean;
  @ViewChild(BaseChartDirective) chart: BaseChartDirective;
  @Input() isZoom: boolean;
  @ViewChild("template", { static: true }) template;

  public barChartLabels: Label[] = [];
  public barChartType: ChartType = "bar";
  public barChartLegend = true;
  public barChartData: ChartDataSets[] = [];
  public lineChartColors: Color[] = [];
  public legends = ['serie1','serie2'];
  private readonly defaultGroupedBarLabels: string[] = ["A", "B", "C", "D"];
  // 45 = diagonal labels, 90 = vertical labels
  private readonly groupedBarLabelRotation = 60;
  private readonly groupedBarLabelColor = "#666666";

  /**
   * Reads the actual rendered x-positions of each bar stack from the chart meta.
   * Returns stacks in order of first appearance and a map of stack → x[] per tick.
   * This is the ground truth for where bars actually are, regardless of barThickness,
   * chart width, or number of periods.
   */
  private getBarStackPositions(chart: any): { orderedStacks: string[], positions: Map<string, number[]> } {
    const positions = new Map<string, number[]>();
    const orderedStacks: string[] = [];

    (chart.data?.datasets || []).forEach((dataset: any, datasetIndex: number) => {
      if (dataset.type === 'bar' && dataset.stack != null && !positions.has(dataset.stack)) {
        const meta = chart.getDatasetMeta(datasetIndex);
        if (meta && meta.data && meta.data.length > 0) {
          const xs: (number | null)[] = meta.data.map((point: any) =>
            point && point._model ? (point._model.x as number) : null
          );
          if (xs.some((x) => x != null)) {
            positions.set(dataset.stack, xs as number[]);
            orderedStacks.push(dataset.stack);
          }
        }
      }
    });

    return { orderedStacks, positions };
  }

  private getGroupedBarLabels(): string[] {
    const labels = this.data?.secondAxisXLabels;
    if (Array.isArray(labels) && labels.length > 0) {
      return labels;
    }

    return this.defaultGroupedBarLabels;
  }

  /**
   * Custom Chart.js plugin to horizontally offset line data points
   *
   * Purpose: Aligns line series bullets with their corresponding columns (A, B, C, D)
   * without modifying the underlying data structure or x-axis configuration.
   *
   * How it works:
   * - Intercepts chart rendering after layout calculation (afterUpdate hook)
   * - Calculates pixel offsets based on chart size (zoomed vs. normal view)
   * - Applies horizontal offsets to line points based on their series index:
   *   * Line 0 (PRODUCT_165) -> Column A (leftmost)
   *   * Line 1 (PRODUCT_166) -> Column B (center-left)
   *   * Line 2 (PRODUCT_167) -> Column C (center-right)
   *   * Line 3 (PRODUCT_168) -> Column D (rightmost)
   *
   * Note: This plugin is registered locally (see ngAfterViewInit) to avoid affecting
   * other charts in the application.
   */
  public lineOffsetPlugin: any = {
    id: 'lineOffsetPlugin',
    afterUpdate: (chart: any) => {
      if (!chart.data || !chart.data.datasets) return;

      // Read where Chart.js actually placed each bar stack — this is the source of
      // truth regardless of barThickness, chart width, or number of periods.
      const { orderedStacks, positions } = this.getBarStackPositions(chart);
      if (orderedStacks.length === 0) return;

      // Move each line point to the x-position of its corresponding bar stack.
      let lineIndex = 0;
      chart.data.datasets.forEach((dataset: any, datasetIndex: number) => {
        if (dataset.type === 'line') {
          const stackKey = orderedStacks[lineIndex % orderedStacks.length];
          const barXs = positions.get(stackKey);
          const datasetMeta = chart.getDatasetMeta(datasetIndex);

          if (datasetMeta && datasetMeta.data && barXs) {
            datasetMeta.data.forEach((point: any, pointIndex: number) => {
              if (point && point._model && barXs[pointIndex] != null) {
                point._model.x = barXs[pointIndex];
              }
            });
          }

          lineIndex++;
        }
      });
    },
    afterDraw: (chart: any) => {
      const xScale = chart.scales && chart.scales["x-axis-0"];
      if (!xScale || !chart.chartArea) return;

      const groupedBarLabels = this.getGroupedBarLabels();
      const rotation = (-this.groupedBarLabelRotation * Math.PI) / 180;
      const y = xScale.bottom - 15;
      const labelsCount = (chart.data && chart.data.labels && chart.data.labels.length) || 0;

      // Use actual bar stack positions so labels are always aligned with bars.
      const { orderedStacks, positions } = this.getBarStackPositions(chart);

      const ctx = chart.ctx;
      ctx.save();
      ctx.fillStyle = this.groupedBarLabelColor;
      ctx.font = "12px Montserrat, sans-serif";
      ctx.textAlign = "right";
      ctx.textBaseline = "top";

      for (let tickIndex = 0; tickIndex < labelsCount; tickIndex++) {
        groupedBarLabels.forEach((label: string, labelIndex: number) => {
          const stackKey = orderedStacks[labelIndex % Math.max(orderedStacks.length, 1)];
          const barXs = stackKey ? positions.get(stackKey) : undefined;
          // Fall back to tick center when bar meta is not yet available.
          const x = (barXs && barXs[tickIndex] != null)
            ? barXs[tickIndex]
            : xScale.getPixelForTick(tickIndex);

          ctx.save();
          ctx.translate(x - 5, y);
          ctx.rotate(rotation);
          ctx.fillText(label, 0, 0);
          ctx.restore();
        });
      }

      ctx.restore();
    }
  };

  public barChartPlugins: any[] = [this.lineOffsetPlugin];

  // Store zoom state for plugin access
  private _chartZoomState = { isZoomed: false };

  public barChartOptions: ChartOptions = {
    aspectRatio: 1.5,
    plugins: {
      datalabels: {
        display: false  // Disable data labels on points
      }
    },
    tooltips: {
      callbacks: {
        title: (items, data) => {
          return this.data.xLabels[items[0].index];
        },
        label: (tooltipItem, data) => {
          let label = data.datasets[tooltipItem.datasetIndex].label || "";

          if (label) {
            label += ": ";
          }
          label +=
            this.data.series[tooltipItem.datasetIndex].yValues[
              tooltipItem.index
            ].yLabel;
          return label;
        },
      },
    },
    legend: {
      labels: {
        generateLabels: function(chart) {
          var legends = [];
          var colors = []
          var datasetIndexes = [];
          var idx = 0;
          chart.data.datasets!.forEach((element:any) => {
            if (legends.indexOf(element.legendGroup) === -1) {
              legends.push(element.legendGroup);
              colors.push(element.backgroundColor.toString());
              datasetIndexes.push(idx);
            }
            idx++;
          });

          return legends.map(function(group: any, index: number) {
            return {
              text: group,
              fillStyle: colors[index],
              datasetIndex: datasetIndexes[index],
              hidden: chart.getDatasetMeta(datasetIndexes[index]).hidden
            };
          });
        }
      },
      onClick: function(e, legendItem) {
        var legendText = legendItem.text;
        var idx = 0;
        this.chart.data.datasets.forEach(function(dataset, i) {
          if (dataset.legendGroup === legendText) {
            var meta = this.chart.getDatasetMeta(idx);
            meta.hidden = !meta.hidden;
          }
          idx++;
        }, this);
        this.chart.update();
      }
    },
    responsive: true,
    scales: {
      xAxes: [
        {
          id: "x-axis-0",
          offset: true,
          gridLines: {
            drawBorder: false,
            zeroLineColor: "transparent",
            display: false,
            drawTicks: false,
            tickMarkLength: 0,
          },
          ticks:{
            // Keep axis height, but draw custom aligned subgroup labels in plugin.
            padding: 0,
            callback: () => " "
          }
        },
        {
          id:'xAxis2',
          type:"category",
          offset: true,
          gridLines: {
            drawOnChartArea: false, // only want the grid lines for one axis to show up
            drawBorder: false,
            drawTicks: false,
            tickMarkLength: 0,
          },
          ticks:{
            padding: 40,
            callback:function(label: string){
              return label;
            }
          }
        }

      ],
      yAxes: [
        {
          id: "y-axis-0",
          ticks: {
            beginAtZero: true,
            callback: (value: number) => {
              if (this.getRoundedValue(value) === value || value > 0) {
                if (this.data.axisY.unit != null) {
                    return value + " " + this.data.axisY.unit;
                }
                return value;
              } 
              return "";
            },
          },
          gridLines: {
            drawBorder: true,
            display: true,
            borderDash: [2, 5],
          },
        },
        {
          id: "y-axis-1",
          position: "right",
          ticks: {
            beginAtZero: true,
            max: 100,
            min: 0,
            fontColor: "#848484",
            callback: (value: number) => {
              if (value % 10 === 0 || value > 0) {
                if (this.data.secondAxisYIsPercentage) {
                  if (this.data.secondAxisYMultiplyBy100) {
                    value = Math.round(value * 1000) / 10;
                  }
                  return value + "%";
                }  else return value;
              }
              return "";
            },
          },
          gridLines: {
            drawBorder: false,
            display: false,
          },
        },
      ],
    },
  };

  zoom() {
    this.dialogService.setZoomInfo(this.data.zoomInfo);
  }

  getRoundedValue(val: number) {
    let roundedValue = 0;
    let sign = 1;
    let multipleOf10 = 1;
    if (val < 0) {
      sign = -1;
      val = Math.abs(val);
    }
    if (val > 0) {
      if (val < 0.1) {
        multipleOf10 = -2;
      } else if (val < 1) {
        multipleOf10 = -1;
      } else if (val < 10) {
        multipleOf10 = 0;
      } else if (val < 100) {
        multipleOf10 = 1;
      } else if (val < 1000) {
        multipleOf10 = 2;
      } else if (val < 10000) {
        multipleOf10 = 3;
      } else if (val < 100000) {
        multipleOf10 = 4;
      } else if (val < 1000000) {
        multipleOf10 = 5;
      }
    }

    roundedValue =
      Math.ceil(val / Math.pow(10, multipleOf10)) * Math.pow(10, multipleOf10);
    return roundedValue * sign;
  }

  ngOnInit() {
    this.viewContainerRef.createEmbeddedView(this.template);

    if (this.data) {
      // Set zoom state for plugin access using the isZoom input
      this._chartZoomState.isZoomed = this.isZoom === true;

      this.barChartData = [];
      this.lineChartColors = [];
      this.barChartLabels = [];
      let data = [];
      let ds = {};
      let color = {};

      this.data.xValues.forEach((v) => {
        this.barChartLabels.push(`P${v}`);
      });

      let minBar = 0;
      let maxBar = 0;
      let minLine = 0;
      let maxLine = 0;

      // Scale bar thickness inversely with the number of periods so bars never
      // exceed their allocated tick width on dense charts (e.g. 8 periods).
      const numPeriods = this.data.xValues.length || 1;
      const baseThickness = this.data.zoomInfo == null ? 20 : 15;
      const barThickness = Math.max(6, Math.round(baseThickness * Math.min(1, 5 / numPeriods)));

      this.data.series.forEach((s) => {
        let min = 0;
        let max = 0;
        data = [];
        s.yValues.forEach((v) => {
          if (v.y < min) {
            min = v.y;
          }
          if (v.y > max) {
            max = v.y;
          }
          data.push(v.y);
        });

        if (s.type === "bar") {
          // tslint:disable-next-line:object-literal-shorthand
          ds = {
            data: data,
            label: s.caption,
            fill: false,
            legendGroup: s.legendGroup,
            stack: s.group,
            type: "bar",
            barThickness: barThickness,
            order: 1,
          };
          if (min < minBar) {
            minBar = min;
          }
          if (max > maxBar) {
            maxBar = max;
          }
        } else {
          if (this.data.secondAxisY) {
            // tslint:disable-next-line:object-literal-shorthand
            ds = {
              data: data,
              label: s.caption,
              legendGroup: s.legendGroup,
              fill: false,
              type: "line",
              yAxisID: "y-axis-1",
              order: 0,
            };
            if (min < minLine) {
              minLine = min;
            }
            if (max > maxLine) {
              maxLine = max;
            }
          } else {
            // tslint:disable-next-line:object-literal-shorthand
            ds = {
              data: data,
              label: s.caption,
              legendGroup: s.legendGroup,
              fill: false,
              type: "line",
              order: 0,
            };
          }
        }

        color = {
          // grey
          backgroundColor: s.color,
          borderColor: s.color,
          pointBackgroundColor: s.color,
          pointBorderColor: s.color,
          pointHoverBackgroundColor: s.color,
          pointHoverBorderColor: s.color,
          borderWidth: 1,
        };

        this.barChartData.push(ds);
        this.lineChartColors.push(color);

        if (!this.data.secondAxisY) {
          this.barChartOptions.scales.yAxes[1].display = false;
        } else {
          this.barChartOptions.scales.yAxes[1].ticks.min = Math.floor(min);
          this.barChartOptions.scales.yAxes[1].ticks.max = Math.ceil(max);
        }
      });

      if (minBar < 0 || minLine < 0) {
        maxBar = this.getRoundedValue(maxBar);
        minBar = this.getRoundedValue(minBar);
        maxLine = this.getRoundedValue(maxLine);
        minLine = this.getRoundedValue(minLine);

        const barRatio = maxBar / (maxBar - minBar);
        const lineRatio = maxLine / (maxLine - minLine);

        if (this.data.secondAxisY && !this.data.secondAxisYFree && !isNaN(lineRatio)) {

          if (lineRatio > barRatio) {
            this.barChartOptions.scales.yAxes[1].ticks.min =
              (maxLine / maxBar) * minBar;
            this.barChartOptions.scales.yAxes[1].ticks.max = maxLine;

            this.barChartOptions.scales.yAxes[0].ticks.min = minBar;
            this.barChartOptions.scales.yAxes[0].ticks.max = maxBar;
          } else {
            this.barChartOptions.scales.yAxes[1].ticks.min = minLine;
            this.barChartOptions.scales.yAxes[1].ticks.max = maxLine;

            this.barChartOptions.scales.yAxes[0].ticks.min = (minBar > 0) ? 0 : minBar;
            this.barChartOptions.scales.yAxes[0].ticks.max = (maxBar < 0) ? 0 : maxBar;
          }
        }
        if (!this.data.secondAxisY) {
          this.barChartOptions.scales.yAxes[0].ticks.min = minBar;
          this.barChartOptions.scales.yAxes[0].ticks.max = maxBar;
        }
        if (this.data.secondAxisY && this.data.secondAxisYFree) {
          this.barChartOptions.scales.yAxes[0].ticks.min = minBar;
          this.barChartOptions.scales.yAxes[0].ticks.max = maxBar;
          this.barChartOptions.scales.yAxes[1].ticks.min = minLine;
          this.barChartOptions.scales.yAxes[1].ticks.max = maxLine;
        }
      }
    }
  }

  /**
   * Register the line offset plugin locally to this chart instance
   *
   * Important: The plugin is registered at the instance level (not globally) to prevent
   * it from affecting other charts in the application. This ensures that only charts
   * created by this component will have line points offset to align with columns.
   */
  ngAfterViewInit() {
    if (this.chart && this.chart.chart) {
      // Initialize plugins array if it doesn't exist
      if (!this.chart.chart.config.plugins) {
        this.chart.chart.config.plugins = [];
      }
      // Add our custom plugin to this specific chart instance
      this.chart.chart.config.plugins.push(this.lineOffsetPlugin);
      // Trigger chart update to apply the plugin
      this.chart.chart.update();
    }
  }
}
